import FileDroppableArea from 'components/atoms/file-dropable-area/FileDroppableArea';
import { FormInput } from 'components/molecules';
import MessageCard from 'components/molecules/card/message/MessageCard';
import MessageForm from 'components/molecules/forms/message/MessageForm';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useElementHeight } from 'hooks/useElementHeight';
import { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  IChatMessage,
  IChatMessageAttachment,
  ICreateThread,
  THREAD_STATUS,
} from 'services/chat/types';
import { useGetClientModuleConfiguration } from 'services/configuration';
import { getPlainText } from 'utils/global/plainText.utils';
import { object, string } from 'yup';

interface ISupportFormProps {
  onSubmit: (values: ICreateThread, formikHelpers: FormikHelpers<ICreateThread>) => void;
  isPending?: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  messages?: IChatMessage[];
  initialValues?: ICreateThread;
  status?: THREAD_STATUS;
}

const SupportForm = ({
  messages = [],
  isLoading,
  onSubmit,
  isFetchingNextPage,
  isPending,
  initialValues,
  status,
}: ISupportFormProps) => {
  const { t } = useTranslation();
  const messageFormRef = useRef<HTMLDivElement>(null);
  const configuration = useGetClientModuleConfiguration();
  const formik = useFormik<ICreateThread>({
    onSubmit,
    enableReinitialize: true,
    initialValues: initialValues ?? {
      subject: '',
      message: '<p></p>',
      attachments: [],
    },
    validationSchema: object().shape({
      subject: string().required(t('formik.required')),
      message: string().required(t('formik.required')),
    }),
  });

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    noClick: true,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: (fileRejections, event) => {
      toast.error(
        t('toast-message.max-file-size-exceed', {
          size: 10,
          unit: 'MB',
          count: fileRejections.length,
        }),
      );
    },
    onDrop: (acceptedFiles) => {
      formik.setFieldValue('attachments', [
        ...formik.values.attachments,
        ...acceptedFiles.filter(
          (file) =>
            !formik.values.attachments.find(
              (attachment) =>
                attachment.name === file.name &&
                attachment.size === file.size &&
                attachment.type === file.type,
            ),
        ),
      ]);
    },
  });

  const footerHeight = useElementHeight(messageFormRef);

  // const topbarTitleProps = useMemo(
  //   () =>
  //     formik.initialValues.subject.length
  //       ? { title: formik.initialValues.subject.length ? formik.initialValues.subject :   <FormInput.Text
  //                 name='subject'
  //                 label={t('input.label.subject')}
  //                 appBackground
  //                 maxLength={50}
  //               />}
  //       : {
  //           topbarContent: (
  //             <div className='flex w-full'>
  //               <FormInput.Text
  //                 name='subject'
  //                 label={t('input.label.subject')}
  //                 appBackground
  //                 maxLength={50}
  //               />
  //             </div>
  //           ),
  //         },
  //   [formik.initialValues.subject],
  // );

  const onRemoveAttachment = (attachment: IChatMessageAttachment, i: number) =>
    formik.setFieldValue(
      'attachments',
      formik.values.attachments.filter((_, index) => index !== i),
    );

  return (
    <FormikProvider value={formik}>
      <form {...getRootProps()}>
        <TopbarPage
          topbarAbsolute
          topbar={
            <TopBar
              path="support"
              title={
                isLoading ? (
                  '-'
                ) : formik.initialValues.subject.length ? (
                  formik.initialValues.subject
                ) : (
                  <div className="max-w-[300px] omitTextTimeLimiter">
                    <FormInput.Text
                      name="subject"
                      label={t('input.label.subject')}
                      appBackground
                      maxLength={50}
                    />
                  </div>
                )
              }
            />
          }
          footer={
            status !== THREAD_STATUS.closed ? (
              <MessageForm
                isPending={isPending}
                customRef={messageFormRef}
                attachments={formik.values.attachments}
                inputProps={getInputProps()}
                openFiles={open}
                onSendMessage={() => formik.handleSubmit()}
                onRemoveAttachment={onRemoveAttachment}
                sendDisabled={
                  !getPlainText(formik.values.message).length && !formik.values.attachments.length
                }
              />
            ) : (
              <div
                className="flex justify-center w-full items-center p-[24px] rounded-md "
                style={{ backgroundColor: 'var(--background)' }}
              >
                <div className="message-form" style={{ display: 'none' }} />
                <p className="opacity-65">
                  {t('support.closed', {
                    name: configuration.data?.clientName ?? 'MagelUP',
                  })}
                </p>
              </div>
            )
          }
        >
          <FileDroppableArea visible={isDragActive} />
          <div style={{ opacity: 0 }}>
            <br />.
            <br />.
            <br />.
            <br />.
          </div>
          <ListWrapper
            items={messages}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            item={(message) => <MessageCard key={message.id} message={message} />}
          />
          <div
            style={{
              marginBottom: footerHeight ? footerHeight - 40 : 0,
            }}
          />
        </TopbarPage>
      </form>
    </FormikProvider>
  );
};

export default SupportForm;
